import type { UploadFile } from 'tdesign-react';
import {
  getUploadPreviewUrl,
  getUploadSubmitUrl,
  isAvatarUploadReady,
} from './AvatarUploadField';
import { MAX_TITLE_COUNT, TITLE_ROW_COUNT } from './config';
import type {
  ConferenceGuestDetail,
  ConferenceRegisterPayload,
  ConferenceTitleItem,
  SignupFormValues,
} from './types';

const DEFAULT_NATIONALITY = '中国';
type MergeableTextField = keyof Pick<
  SignupFormValues,
  | 'nameCn'
  | 'nameEn'
  | 'nationality'
  | 'phone'
  | 'organization'
  | 'position'
  | 'bioCn'
  | 'bioEn'
>;

const MERGEABLE_TEXT_FIELDS: MergeableTextField[] = [
  'nameCn',
  'nameEn',
  'nationality',
  'phone',
  'organization',
  'position',
  'bioCn',
  'bioEn',
];

export const trimText = (value: unknown) => String(value ?? '').trim();

export const createEmptyTitleRow = (): ConferenceTitleItem => ({
  titleCn: '',
  titleEn: '',
});

export const createDefaultFormValues = (): SignupFormValues => ({
  avatar: [],
  nameCn: '',
  nameEn: '',
  titles: Array.from({ length: TITLE_ROW_COUNT }, () => createEmptyTitleRow()),
  nationality: DEFAULT_NATIONALITY,
  phone: '',
  email: '',
  organization: '',
  position: '',
  bioCn: '',
  bioEn: '',
  allowContact: 1,
});

export const createTitleRows = (value: unknown): ConferenceTitleItem[] => {
  const normalized = Array.isArray(value)
    ? value
        .map((item) => ({
          titleCn: trimText((item as Record<string, unknown>)?.titleCn),
          titleEn: trimText((item as Record<string, unknown>)?.titleEn),
        }))
        .filter((item) => item.titleCn || item.titleEn)
        .slice(0, MAX_TITLE_COUNT)
    : [];

  while (normalized.length < TITLE_ROW_COUNT) {
    normalized.push(createEmptyTitleRow());
  }

  return normalized;
};

export const getFilledTitleItems = (value: unknown): ConferenceTitleItem[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => ({
      titleCn: trimText((item as Record<string, unknown>)?.titleCn),
      titleEn: trimText((item as Record<string, unknown>)?.titleEn),
    }))
    .filter((item) => item.titleCn || item.titleEn);
};

export const createInitialAvatarFiles = (avatar: unknown) => {
  const avatarUrl = getUploadPreviewUrl(avatar);

  if (!avatarUrl) {
    return [] as UploadFile[];
  }

  return [
    {
      name: 'avatar.png',
      status: 'success',
      url: avatarUrl,
    } as UploadFile,
  ];
};

export const buildStaticInitialValues = (
  userName: string,
  info: Record<string, unknown>
): Partial<SignupFormValues> => {
  return {
    nameCn: trimText(info.NameCn ?? info.nameCn),
    nationality:
      trimText(info.Nationality ?? info.nationality) || DEFAULT_NATIONALITY,
    phone: trimText(info.Mobile ?? info.Phone),
    // 邮箱优先从 info 中获取预填，确保必填字段有初始值
    email: trimText(info.Email ?? info.email),
    organization: trimText(info.Department),
    position: trimText(info.Position),
  };
};

export const buildGuestDetailInitialValues = (
  detail: ConferenceGuestDetail
): Partial<SignupFormValues> => ({
  avatar: createInitialAvatarFiles(detail.avatar),
  nameCn: trimText(detail.nameCn),
  nameEn: trimText(detail.nameEn),
  titles: createTitleRows(detail.titles),
  nationality: trimText(detail.nationality),
  phone: trimText(detail.phone),
  email: trimText(detail.email),
  organization: trimText(detail.organization),
  position: trimText(detail.position),
  bioCn: trimText(detail.bioCn),
  bioEn: trimText(detail.bioEn),
  allowContact: detail.allowContact === 0 ? 0 : 1,
});

export const mergeInitialValues = (
  ...sources: Array<Partial<SignupFormValues> | undefined>
) => {
  const base = createDefaultFormValues();

  for (const item of sources) {
    if (!item) continue;
    if (item.avatar?.length) {
      base.avatar = item.avatar;
    }
    if (item.titles?.length) {
      base.titles = createTitleRows(item.titles);
    }

    for (const field of MERGEABLE_TEXT_FIELDS) {
      const value = item[field];
      if (hasTextValue(value)) {
        base[field] = trimText(value);
      }
    }

    // email 单独处理：允许空字符串覆盖，确保表单值优先
    if (item.email !== undefined) {
      base.email = trimText(item.email);
    }

    if (typeof item.allowContact === 'number') {
      base.allowContact = item.allowContact === 0 ? 0 : 1;
    }
  }

  return base;
};

export const buildConferenceRegisterPayload = (
  values: Partial<SignupFormValues>,
  finalEmail: string
): ConferenceRegisterPayload => ({
  // 展示继续使用预览图 URL，新上传头像提交给后端时只保留 S大会资源半路径。
  avatar: getUploadSubmitUrl(values.avatar),
  nameCn: trimText(values.nameCn),
  nameEn: trimText(values.nameEn),
  titles: getFilledTitleItems(values.titles),
  nationality: trimText(values.nationality),
  phone: trimText(values.phone),
  email: finalEmail,
  organization: trimText(values.organization),
  position: trimText(values.position),
  bioCn: trimText(values.bioCn),
  bioEn: trimText(values.bioEn),
  allowContact: values.allowContact === 0 ? 0 : 1,
});

const normalizeComparableTitleItems = (value: unknown) =>
  getFilledTitleItems(value).map((item) => ({
    titleCn: trimText(item.titleCn),
    titleEn: trimText(item.titleEn),
  }));

const buildComparableConferenceRegisterPayload = (
  payload: ConferenceRegisterPayload
): ConferenceRegisterPayload => ({
  ...payload,
  avatar: trimText(payload.avatar),
  nameCn: trimText(payload.nameCn),
  nameEn: trimText(payload.nameEn),
  titles: normalizeComparableTitleItems(payload.titles),
  nationality: trimText(payload.nationality),
  phone: trimText(payload.phone),
  email: trimText(payload.email),
  organization: trimText(payload.organization),
  position: trimText(payload.position),
  bioCn: trimText(payload.bioCn),
  bioEn: trimText(payload.bioEn),
  allowContact: payload.allowContact === 0 ? 0 : 1,
});

export const isConferenceRegisterPayloadChanged = (
  current: ConferenceRegisterPayload,
  initial: ConferenceRegisterPayload
) => {
  // 会聊资料更新以最终提交给接口的 payload 为准，避免头像组件对象、头衔空行等造成误判。
  const currentComparable = buildComparableConferenceRegisterPayload(current);
  const initialComparable = buildComparableConferenceRegisterPayload(initial);

  return (
    JSON.stringify(currentComparable) !== JSON.stringify(initialComparable)
  );
};

export const formatMaskedPhone = (value: string) => {
  const text = value.trim();

  if (!text) {
    return '';
  }

  if (text.includes('*')) {
    return text;
  }

  const digits = text.replace(/\D/g, '');

  if (digits.length === 11) {
    return `${digits.slice(0, 3)}****${digits.slice(-4)}`;
  }

  return text;
};

export const hasTextValue = (value: unknown) => trimText(value).length > 0;

export const isSignupFormSubmittable = (
  values: Partial<SignupFormValues> | undefined
) => {
  if (!values) {
    return false;
  }

  const titles = getFilledTitleItems(values.titles);
  const [firstTitle] = titles;

  if (!isAvatarUploadReady(values.avatar)) {
    return false;
  }

  if (!hasTextValue(values.nameCn) || !hasTextValue(values.nameEn)) {
    return false;
  }

  if (
    !hasTextValue(firstTitle?.titleCn) ||
    !hasTextValue(firstTitle?.titleEn)
  ) {
    return false;
  }

  if (
    !hasTextValue(values.nationality) ||
    !hasTextValue(values.phone) ||
    !hasTextValue(values.email)
  ) {
    return false;
  }

  if (!hasTextValue(values.bioCn) || !hasTextValue(values.bioEn)) {
    return false;
  }

  return values.allowContact === 0 || values.allowContact === 1;
};
